/* ============================================
   Artemis Theme — Collection Filters
   Custom element: <collection-filters>
   App-free client-side faceted filtering.

   Faceted mode (collection pages with [data-facet-config]):
     - Hydrates the full product set via the Section Rendering API.
     - Builds the filter sidebar from the facet config + each card's
       data-facets attribute.
     - Filters/sorts entirely client-side (no page reload), syncs URL.

   Legacy mode (e.g. search page, no [data-facet-config]):
     - Only wires the mobile drawer + collapse/truncation/in-filter search.
       Sort + applied-filter chips fall back to normal navigation.
   ============================================ */

(function () {
  'use strict';

  if (customElements.get('collection-filters')) return;

  var COLLAPSE_STORE_KEY = 'artemis-filter-collapse';
  var CACHE_PREFIX = 'artemis-facets:';
  var RANGE_DEBOUNCE_MS = 300;
  var POOL_SIZE = 4;

  var ICON_CHEVRON =
    '<svg class="icon icon-chevron-down filter-group__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>';
  var ICON_CLOSE =
    '<svg class="icon icon-close filter-chip__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';

  // Rough garment-size ordering for the "size" facet (falls back to alpha).
  // Both abbreviated ("2xl") and spelled-out ("2x-large") forms are listed so
  // either label style resolves; matching is separator-insensitive below.
  var SIZE_ORDER = [
    'xxxs', 'xxs', 'xs', 's', 'small', 'm', 'medium', 'l', 'large',
    'xl', 'x-large', 'xlarge',
    'xxl', '2xl', '2x-large',
    'xxxl', '3xl', '3x-large',
    'xxxxl', '4xl', '4x-large',
    'xxxxxl', '5xl', '5x-large',
    '6xl', '6x-large',
    'os', 'one size', 'osfa'
  ];

  // Length modifiers (Tall, Big, Long) that pair with a base size. Each pushes
  // the size into a later group so all plain sizes sort before all Tall ones,
  // while the base size still orders within the group.
  var SIZE_MODIFIERS = ['tall', 'big', 'long'];

  function sizeRank(size) {
    var key = String(size == null ? '' : size).trim().toLowerCase();
    var idx = SIZE_ORDER.indexOf(key);
    if (idx >= 0) return idx;
    for (var m = 0; m < SIZE_MODIFIERS.length; m++) {
      var mod = SIZE_MODIFIERS[m];
      var re = new RegExp('(^' + mod + '\\b|\\b' + mod + '$)', 'g');
      if (re.test(key)) {
        // Match the base separator-insensitively so "x-large"/"x large" and
        // "2x-large"/"2x large" all resolve regardless of how the size is spelled.
        var base = key.replace(re, '').replace(/[\s-]+/g, '');
        var baseRank = SIZE_ORDER.length;
        for (var b = 0; b < SIZE_ORDER.length; b++) {
          if (SIZE_ORDER[b].replace(/[\s-]+/g, '') === base) { baseRank = b; break; }
        }
        return SIZE_ORDER.length * (m + 1) + baseRank;
      }
    }
    return SIZE_ORDER.length * (SIZE_MODIFIERS.length + 2);
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  class CollectionFilters extends HTMLElement {
    constructor() {
      super();
      this._rangeTimer = null;
      this._onPopState = this._onPopState.bind(this);
      this.faceted = false;
    }

    connectedCallback() {
      this.currency =
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';

      this._bindShell();

      var cfgEl = this.querySelector('[data-facet-config]');
      if (cfgEl) {
        this._initFaceted(cfgEl);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this._onPopState);
    }

    /* =========================================================
       Shared / presentational wiring (both modes)
       ========================================================= */

    _bindShell() {
      var self = this;

      this.addEventListener('click', function (e) {
        var openBtn = e.target.closest('[data-filter-drawer-open]');
        if (openBtn) { e.preventDefault(); self._openDrawer(); return; }

        var closeBtn = e.target.closest('[data-filter-drawer-close]');
        if (closeBtn) { e.preventDefault(); self._closeDrawer(); return; }

        var toggle = e.target.closest('[data-filter-toggle]');
        if (toggle) { e.preventDefault(); self._toggleGroupTruncation(toggle); return; }

        if (self.faceted) {
          var chip = e.target.closest('[data-filter-chip]');
          if (chip) { e.preventDefault(); self._removeChip(chip); return; }

          var clear = e.target.closest('[data-filter-clear]');
          if (clear) { e.preventDefault(); self._clearAll(); return; }
        }
      });

      this.addEventListener('input', function (e) {
        if (e.target.matches('[data-filter-search]')) {
          self._filterValuesInGroup(e.target);
          return;
        }
        if (self.faceted && e.target.matches('[data-filter-range-min], [data-filter-range-max]')) {
          self._onRangeInput(e.target);
        }
      });

      if (this.faceted === false) {
        // bind once for server-rendered groups (search page)
        this._bindCollapseState();
      }
    }

    /* =========================================================
       Faceted mode
       ========================================================= */

    _initFaceted(cfgEl) {
      try {
        this.config = JSON.parse(cfgEl.textContent || '[]');
      } catch (e) {
        this.config = [];
      }
      if (!this.config.length) return;

      var mapEl = this.querySelector('[data-swatch-map]');
      try {
        this.swatchMap = mapEl ? JSON.parse(mapEl.textContent || '{}') : {};
      } catch (e) {
        this.swatchMap = {};
      }

      this.grid = this.querySelector('[data-filter-grid]');
      if (!this.grid) return;

      this.collectionUrl = this.getAttribute('data-collection-url') || window.location.pathname;
      this.sectionId = this.getAttribute('data-section-id');
      this.totalCount = parseInt(this.getAttribute('data-product-count'), 10) || 0;
      this.pageSize = parseInt(this.getAttribute('data-page-size'), 10) || 50;
      this.maxClient = parseInt(this.getAttribute('data-max-clientside'), 10) || 250;
      this.sidebarPos = this.getAttribute('data-sidebar-position') || 'left';
      this.showCounts = this.getAttribute('data-show-counts') === 'true';

      var countEl = this.querySelector('[data-filter-count]');
      this._countNoun =
        (countEl ? (countEl.textContent || '').replace(/^[\s\d.,]+/, '').trim() : '') || 'products';
      // Announce subsequent count changes ("N products") to assistive tech (WCAG 4.1.3).
      if (countEl) {
        countEl.setAttribute('role', 'status');
        countEl.setAttribute('aria-live', 'polite');
      }

      // Collections larger than the cap keep standard server pagination.
      if (this.totalCount > this.maxClient) return;

      this.faceted = true;

      // Optional localized UI strings injected by the section.
      this.i18n = this._parseI18n();

      var sort = this.querySelector('[data-filter-sort]');
      if (sort) sort.removeAttribute('onchange');

      window.addEventListener('popstate', this._onPopState);
      this.setAttribute('aria-busy', 'true');
      // The aria-busy host has no role, so the busy state isn't announced on
      // its own — drive a dedicated live region instead (WCAG 4.1.3).
      this._announceStatus(this._t('loading', 'Loading products…'));

      var self = this;
      this._hydrate()
        .catch(function () { /* proceed with whatever loaded */ })
        .then(function () {
          self.removeAttribute('aria-busy');
          self._buildIndex();
          self._renderFacets();
          self._bindFacetInputs();
          self._bindCollapseState();
          self.state = self._parseUrl();
          self._applyStateToInputs();
          self._applySort();
          self._bindSort();
          self._render();
          self._revealSidebar();
          var pg = self.querySelector('[data-server-pagination]');
          if (pg) pg.hidden = true;
          self.grid.classList.add('is-visible');
          // Clear the loading message; the [data-filter-count] live region
          // (set in _render) carries the "Showing N products" result instead.
          self._announceStatus('');
          self.dispatchEvent(new CustomEvent('collection-filters:ready', { bubbles: true }));
        });
    }

    /* --- Hydration: load every product into the grid --- */

    _cards() {
      return Array.prototype.slice.call(this.grid.querySelectorAll(':scope > [data-product-card]'));
    }

    _hydrate() {
      var rendered = this._cards().length;
      if (this.totalCount <= rendered) return Promise.resolve();

      var cached = this._readCache();
      if (cached && cached.length) {
        this._appendCardsHtml(cached);
        return Promise.resolve();
      }

      var pages = Math.ceil(Math.min(this.totalCount, this.maxClient) / this.pageSize);
      var queue = [];
      for (var p = 2; p <= pages; p++) queue.push(p);

      var results = {};
      var self = this;
      return this._pool(queue, POOL_SIZE, function (page) {
        return self._fetchPage(page).then(function (htmls) { results[page] = htmls; });
      }).then(function () {
        var all = [];
        for (var i = 2; i <= pages; i++) {
          if (results[i]) all = all.concat(results[i]);
        }
        self._appendCardsHtml(all);
        self._writeCache(all);
      });
    }

    _fetchPage(page) {
      var url =
        this.collectionUrl +
        '?section_id=' + encodeURIComponent(this.sectionId) +
        '&page=' + page;
      return fetch(url, { headers: { Accept: 'text/html' } })
        .then(function (res) {
          if (!res.ok) throw new Error('page ' + page + ': ' + res.status);
          return res.text();
        })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var cards = doc.querySelectorAll('[data-filter-grid] [data-product-card]');
          return Array.prototype.map.call(cards, function (c) { return c.outerHTML; });
        });
    }

    _appendCardsHtml(htmls) {
      if (!htmls || !htmls.length) return;
      var frag = document.createDocumentFragment();
      var tmp = document.createElement('div');
      for (var i = 0; i < htmls.length; i++) {
        tmp.innerHTML = htmls[i];
        var card = tmp.firstElementChild;
        if (card) frag.appendChild(card);
      }
      this.grid.appendChild(frag);
    }

    _pool(items, size, worker) {
      var i = 0;
      function next() {
        if (i >= items.length) return Promise.resolve();
        var item = items[i++];
        return Promise.resolve(worker(item)).catch(function () {}).then(next);
      }
      var runners = [];
      for (var k = 0; k < Math.min(size, items.length); k++) runners.push(next());
      return Promise.all(runners);
    }

    _cacheKey() {
      return CACHE_PREFIX + window.location.pathname + ':' + this.totalCount;
    }

    _readCache() {
      try {
        var raw = sessionStorage.getItem(this._cacheKey());
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    }

    _writeCache(htmls) {
      try {
        var json = JSON.stringify(htmls);
        if (json.length > 2000000) return; // ~2MB guard
        sessionStorage.setItem(this._cacheKey(), json);
      } catch (e) { /* quota — ignore */ }
    }

    /* --- Build the in-memory product index --- */

    _norm(v) {
      return String(v == null ? '' : v).trim().toLowerCase();
    }

    _buildIndex() {
      var self = this;
      this.labels = {}; // facetId -> { lower: displayLabel }
      this.priceMin = Infinity;
      this.priceMax = 0;

      this.products = this._cards().map(function (el, order) {
        var facets = {};
        var raw = el.getAttribute('data-facets');
        if (raw) {
          try {
            var obj = JSON.parse(raw);
            for (var key in obj) {
              if (!obj.hasOwnProperty(key)) continue;
              var set = new Set();
              (obj[key] || []).forEach(function (val) {
                var lower = self._norm(val);
                if (!lower) return;
                set.add(lower);
                if (!self.labels[key]) self.labels[key] = {};
                if (!self.labels[key][lower]) self.labels[key][lower] = String(val).trim();
              });
              facets[key] = set;
            }
          } catch (e) { /* skip malformed */ }
        }
        var price = parseInt(el.getAttribute('data-price'), 10) || 0;
        if (price < self.priceMin) self.priceMin = price;
        if (price > self.priceMax) self.priceMax = price;
        return {
          el: el,
          facets: facets,
          price: price,
          title: (el.getAttribute('data-title') || '').toLowerCase(),
          created: parseInt(el.getAttribute('data-created'), 10) || 0,
          order: order
        };
      });

      if (this.priceMin === Infinity) this.priceMin = 0;
    }

    /* --- Render the sidebar from config + index --- */

    _renderFacets() {
      var html = '';
      for (var i = 0; i < this.config.length; i++) {
        var cfg = this.config[i];
        if (cfg.type === 'price') {
          html += this._priceGroupHtml(cfg);
        } else {
          html += this._listGroupHtml(cfg);
        }
      }
      var form = this.querySelector('[data-filter-form]');
      if (form) {
        form.innerHTML = html;
        if (window.ArtemisSwatch) window.ArtemisSwatch.paint(form);
      }
    }

    _facetEntries(cfg) {
      var self = this;
      var map = this.labels[cfg.id] || {};
      var entries = Object.keys(map).map(function (lower) {
        return { lower: lower, label: map[lower], base: self._countValue(cfg.id, lower, false) };
      });

      if (cfg.sort === 'alpha') {
        entries.sort(function (a, b) { return a.label.localeCompare(b.label); });
      } else if (cfg.sort === 'size' || cfg.id === 'size') {
        entries.sort(function (a, b) {
          return sizeRank(a.lower) - sizeRank(b.lower) || a.label.localeCompare(b.label);
        });
      } else {
        entries.sort(function (a, b) { return b.base - a.base || a.label.localeCompare(b.label); });
      }
      return entries;
    }

    _listGroupHtml(cfg) {
      var entries = this._facetEntries(cfg);
      if (!entries.length) return '';

      var self = this;
      var truncate = cfg.truncate || 8;
      var isSwatch = cfg.display === 'swatch';
      var searchLabel = self._t('search', 'Search') + ' ' + esc(cfg.label);
      var searchHtml =
        entries.length > truncate
          ? '<label class="filter-group__search"><span class="visually-hidden">' + searchLabel +
            '</span><input type="search" class="input filter-group__search-input" ' +
            'data-filter-search placeholder="' + searchLabel + '" autocomplete="off"></label>'
          : '';

      var items = entries.map(function (entry, idx) {
        var hidden = idx >= truncate ? ' is-hidden' : '';
        var trunc = idx >= truncate ? ' data-truncated' : '';
        var inner;
        if (isSwatch) {
          var color = self.swatchMap[entry.lower] || entry.label;
          inner =
            '<label class="filter-swatch">' +
              '<input type="checkbox" class="visually-hidden" data-facet="' + esc(cfg.id) + '" value="' + esc(entry.lower) + '">' +
              '<span class="filter-swatch__dot" data-swatch-name="' + esc(entry.label) + '" style="background:' + esc(color) + ';" aria-hidden="true"></span>' +
              '<span class="filter-swatch__name">' + esc(entry.label) + '</span>' +
            '</label>';
        } else {
          var count = self.showCounts
            ? '<span class="filter-checkbox__count" data-filter-value-count>(' + entry.base + ')</span>'
            : '';
          inner =
            '<label class="filter-checkbox">' +
              '<input type="checkbox" data-facet="' + esc(cfg.id) + '" value="' + esc(entry.lower) + '">' +
              '<span class="filter-checkbox__label">' + esc(entry.label) + '</span>' +
              count +
            '</label>';
        }
        return (
          '<li class="filter-group__item' + hidden + '" data-filter-item data-filter-value="' +
          esc(entry.lower) + '"' + trunc + '>' + inner + '</li>'
        );
      }).join('');

      var toggleHtml =
        entries.length > truncate
          ? '<button type="button" class="filter-group__toggle link" data-filter-toggle aria-expanded="false">' +
            '<span data-filter-toggle-more>' + esc(self._t('show_more', 'Show more')) + '</span>' +
            '<span data-filter-toggle-less hidden>' + esc(self._t('show_less', 'Show less')) + '</span></button>'
          : '';

      return (
        '<details class="filter-group" data-filter-group data-filter-key="' + esc(cfg.id) + '" open>' +
          '<summary class="filter-group__summary">' +
            '<span class="filter-group__label">' + esc(cfg.label) + '</span>' +
            '<span class="filter-group__active-count" data-filter-active-group hidden></span>' +
            ICON_CHEVRON +
          '</summary>' +
          '<div class="filter-group__body">' +
            searchHtml +
            '<ul class="filter-group__list' + (isSwatch ? ' filter-group__list--swatches' : '') +
              '" role="list" data-filter-list>' + items + '</ul>' +
            toggleHtml +
          '</div>' +
        '</details>'
      );
    }

    _priceGroupHtml(cfg) {
      var maxDollars = Math.ceil(this.priceMax / 100);
      return (
        '<details class="filter-group" data-filter-group data-filter-key="price" open>' +
          '<summary class="filter-group__summary">' +
            '<span class="filter-group__label">' + esc(cfg.label || 'Price') + '</span>' +
            '<span class="filter-group__active-count" data-filter-active-group hidden></span>' +
            ICON_CHEVRON +
          '</summary>' +
          '<div class="filter-group__body">' +
            '<div class="filter-range" data-filter-range>' +
              '<div class="filter-range__inputs">' +
                '<label class="filter-range__field"><span class="filter-range__field-label">' + esc(this._t('price_from', 'From')) + '</span>' +
                  '<input type="number" class="input filter-range__input" data-filter-range-min ' +
                  'min="0" max="' + maxDollars + '" step="1" inputmode="decimal" placeholder="0"></label>' +
                '<span class="filter-range__separator" aria-hidden="true">—</span>' +
                '<label class="filter-range__field"><span class="filter-range__field-label">' + esc(this._t('price_to', 'To')) + '</span>' +
                  '<input type="number" class="input filter-range__input" data-filter-range-max ' +
                  'min="0" max="' + maxDollars + '" step="1" inputmode="decimal" placeholder="' + maxDollars + '"></label>' +
              '</div>' +
              '<div class="filter-range__track" aria-hidden="true">' +
                '<div class="filter-range__fill" data-filter-range-fill style="--min-pct:0%;--max-pct:100%;"></div>' +
              '</div>' +
              '<p class="filter-range__hint">' + esc(this._money(0)) + ' – ' + esc(this._money(this.priceMax)) + '</p>' +
            '</div>' +
          '</div>' +
        '</details>'
      );
    }

    _money(cents) {
      var amount = (cents || 0) / 100;
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency }).format(amount);
      } catch (e) {
        return '$' + amount.toFixed(2);
      }
    }

    _parseI18n() {
      var el = this.querySelector('[data-filter-i18n]');
      if (!el) return {};
      try { return JSON.parse(el.textContent || '{}'); } catch (e) { return {}; }
    }

    _t(key, fallback) {
      return (this.i18n && this.i18n[key]) || fallback;
    }

    _bindFacetInputs() {
      var self = this;
      this.addEventListener('change', function (e) {
        if (!self.faceted) return;
        var t = e.target;
        if (!t.matches('input[data-facet]')) return;
        var id = t.getAttribute('data-facet');
        var val = self._norm(t.value);
        if (!self.state.filters[id]) self.state.filters[id] = new Set();
        if (t.checked) self.state.filters[id].add(val);
        else self.state.filters[id].delete(val);
        self._afterChange();
      });
    }

    _onRangeInput(input) {
      var self = this;
      var minEl = this.querySelector('[data-filter-range-min]');
      var maxEl = this.querySelector('[data-filter-range-max]');
      clearTimeout(this._rangeTimer);
      this._rangeTimer = setTimeout(function () {
        self.state.price.min = minEl && minEl.value !== '' ? parseFloat(minEl.value) : null;
        self.state.price.max = maxEl && maxEl.value !== '' ? parseFloat(maxEl.value) : null;
        self._afterChange();
      }, RANGE_DEBOUNCE_MS);
    }

    /* --- State <-> URL --- */

    _defaultSort() {
      var sort = this.querySelector('[data-filter-sort]');
      if (!sort) return 'manual';
      var opt = sort.options[sort.selectedIndex];
      return (opt && opt.getAttribute('data-sort-value')) || 'manual';
    }

    _parseUrl() {
      var params = new URLSearchParams(window.location.search);
      var filters = {};
      for (var i = 0; i < this.config.length; i++) {
        var cfg = this.config[i];
        if (cfg.type === 'price') continue;
        var raw = params.get(cfg.param);
        if (raw) {
          var set = new Set();
          raw.split(',').forEach(function (s) {
            var v = s.trim().toLowerCase();
            if (v) set.add(v);
          });
          if (set.size) filters[cfg.id] = set;
        }
      }
      var price = { min: null, max: null };
      var pr = params.get('price');
      if (pr) {
        var parts = pr.split('-');
        price.min = parts[0] ? parseFloat(parts[0]) : null;
        price.max = parts[1] ? parseFloat(parts[1]) : null;
      }
      return { filters: filters, price: price, sort: params.get('sort_by') || this._defaultSort() };
    }

    _syncUrl() {
      var params = new URLSearchParams(window.location.search);
      params.delete('page');
      for (var i = 0; i < this.config.length; i++) {
        var cfg = this.config[i];
        if (cfg.type === 'price') { params.delete('price'); continue; }
        params.delete(cfg.param);
      }
      for (var j = 0; j < this.config.length; j++) {
        var c = this.config[j];
        if (c.type === 'price') {
          var p = this.state.price;
          if (p.min != null || p.max != null) {
            params.set('price', (p.min != null ? p.min : '') + '-' + (p.max != null ? p.max : ''));
          }
          continue;
        }
        var s = this.state.filters[c.id];
        if (s && s.size) params.set(c.param, Array.from(s).join(','));
      }
      if (this.state.sort) params.set('sort_by', this.state.sort);
      var qs = params.toString();
      history.replaceState({ filters: true }, '', window.location.pathname + (qs ? '?' + qs : ''));
    }

    _applyStateToInputs() {
      var self = this;
      this.querySelectorAll('input[data-facet]').forEach(function (input) {
        var set = self.state.filters[input.getAttribute('data-facet')];
        input.checked = !!(set && set.has(self._norm(input.value)));
      });
      var minEl = this.querySelector('[data-filter-range-min]');
      var maxEl = this.querySelector('[data-filter-range-max]');
      if (minEl) minEl.value = this.state.price.min != null ? this.state.price.min : '';
      if (maxEl) maxEl.value = this.state.price.max != null ? this.state.price.max : '';

      var sort = this.querySelector('[data-filter-sort]');
      if (sort) {
        for (var i = 0; i < sort.options.length; i++) {
          if (sort.options[i].getAttribute('data-sort-value') === this.state.sort) {
            sort.selectedIndex = i;
            break;
          }
        }
      }
    }

    _onPopState() {
      if (!this.faceted) return;
      this.state = this._parseUrl();
      this._applyStateToInputs();
      this._applySort();
      this._render();
    }

    _afterChange() {
      this._render();
      this._syncUrl();
    }

    /* --- Matching + counts --- */

    _matchesExcept(p, exceptId) {
      for (var id in this.state.filters) {
        if (!this.state.filters.hasOwnProperty(id)) continue;
        if (id === exceptId) continue;
        var set = this.state.filters[id];
        if (!set || !set.size) continue;
        var pv = p.facets[id];
        if (!pv) return false;
        var ok = false;
        var values = Array.from(set);
        for (var k = 0; k < values.length; k++) {
          if (pv.has(values[k])) { ok = true; break; }
        }
        if (!ok) return false;
      }
      if (exceptId !== 'price') {
        var pr = this.state.price;
        if (pr.min != null && p.price < pr.min * 100) return false;
        if (pr.max != null && p.price > pr.max * 100) return false;
      }
      return true;
    }

    _matches(p) {
      return this._matchesExcept(p, '__none__');
    }

    _countValue(facetId, value, live) {
      var count = 0;
      for (var i = 0; i < this.products.length; i++) {
        var p = this.products[i];
        if (live && !this._matchesExcept(p, facetId)) continue;
        var set = p.facets[facetId];
        if (set && set.has(value)) count++;
      }
      return count;
    }

    /* --- Render --- */

    _render() {
      var self = this;
      var visible = 0;

      this.products.forEach(function (p) {
        var show = self._matches(p);
        p.el.classList.toggle('is-hidden', !show);
        if (show) visible++;
      });

      var countEl = this.querySelector('[data-filter-count]');
      if (countEl) countEl.textContent = visible + ' ' + this._countNoun;

      // Per-value live counts + disabled state, per list group.
      this.querySelectorAll('[data-filter-group]').forEach(function (group) {
        var id = group.getAttribute('data-filter-key');
        if (id === 'price') return;
        var checkedCount = 0;
        group.querySelectorAll('[data-filter-item]').forEach(function (item) {
          var value = item.getAttribute('data-filter-value');
          var input = item.querySelector('input[data-facet]');
          var n = self._countValue(id, value, true);
          var countSpan = item.querySelector('[data-filter-value-count]');
          if (countSpan) countSpan.textContent = '(' + n + ')';
          var label = item.querySelector('.filter-checkbox, .filter-swatch');
          var disabled = n === 0 && input && !input.checked;
          if (label) label.classList.toggle('is-disabled', disabled);
          if (input) {
            input.disabled = disabled;
            if (input.checked) {
              checkedCount++;
              item.classList.remove('is-hidden'); // keep active values visible
            }
          }
        });
        self._setGroupActiveCount(group, checkedCount);
      });

      // Price group active badge + fill.
      this._renderPriceState();

      this._renderChips();
      this._renderActiveTotal();

      var empty = this.querySelector('[data-filter-empty]');
      if (empty) empty.hidden = visible !== 0;
    }

    _setGroupActiveCount(group, n) {
      var badge = group.querySelector('[data-filter-active-group]');
      if (!badge) return;
      if (n > 0) { badge.textContent = n; badge.hidden = false; }
      else { badge.hidden = true; }
    }

    _renderPriceState() {
      var group = this.querySelector('[data-filter-group][data-filter-key="price"]');
      if (!group) return;
      var pr = this.state.price;
      var active = pr.min != null || pr.max != null;
      this._setGroupActiveCount(group, active ? 1 : 0);

      var fill = group.querySelector('[data-filter-range-fill]');
      if (fill) {
        var maxCents = this.priceMax || 1;
        var minPct = pr.min != null ? Math.max(0, Math.min(100, (pr.min * 100 / maxCents) * 100)) : 0;
        var maxPct = pr.max != null ? Math.max(0, Math.min(100, (pr.max * 100 / maxCents) * 100)) : 100;
        fill.style.setProperty('--min-pct', minPct + '%');
        fill.style.setProperty('--max-pct', maxPct + '%');
      }
    }

    _renderActiveTotal() {
      var total = 0;
      for (var id in this.state.filters) {
        if (this.state.filters.hasOwnProperty(id) && this.state.filters[id] && this.state.filters[id].size) {
          total += this.state.filters[id].size;
        }
      }
      if (this.state.price.min != null || this.state.price.max != null) total++;

      var badge = this.querySelector('[data-filter-active-count]');
      if (badge) {
        if (total > 0) { badge.textContent = total; badge.hidden = false; }
        else { badge.hidden = true; }
      }
    }

    /* --- Chips --- */

    _labelFor(facetId) {
      for (var i = 0; i < this.config.length; i++) {
        if (this.config[i].id === facetId) return this.config[i].label;
      }
      return facetId;
    }

    _renderChips() {
      var container = this.querySelector('[data-filter-chips]');
      if (!container) return;

      var chips = [];
      for (var i = 0; i < this.config.length; i++) {
        var cfg = this.config[i];
        if (cfg.type === 'price') continue;
        var set = this.state.filters[cfg.id];
        if (!set || !set.size) continue;
        var labelMap = this.labels[cfg.id] || {};
        var self = this;
        Array.from(set).forEach(function (val) {
          var display = labelMap[val] || val;
          chips.push(
            '<button type="button" class="filter-chip" data-filter-chip data-facet="' + esc(cfg.id) +
            '" data-value="' + esc(val) + '" aria-label="Remove filter ' + esc(display) + '">' +
              '<span class="filter-chip__text"><span class="filter-chip__group">' + esc(cfg.label) +
              ':</span> <span class="filter-chip__value">' + esc(display) + '</span></span>' +
              ICON_CLOSE +
            '</button>'
          );
        });
      }

      var pr = this.state.price;
      if (pr.min != null || pr.max != null) {
        var priceLabel =
          (pr.min != null ? this._money(pr.min * 100) : this._money(0)) + ' – ' +
          (pr.max != null ? this._money(pr.max * 100) : this._money(this.priceMax));
        chips.push(
          '<button type="button" class="filter-chip" data-filter-chip data-facet="price" ' +
          'aria-label="Remove price filter">' +
            '<span class="filter-chip__text"><span class="filter-chip__group">' +
            esc(this._labelFor('price')) + ':</span> <span class="filter-chip__value">' +
            esc(priceLabel) + '</span></span>' + ICON_CLOSE +
          '</button>'
        );
      }

      if (!chips.length) {
        container.hidden = true;
        container.innerHTML = '';
        return;
      }

      container.hidden = false;
      container.innerHTML =
        '<span class="collection-chips__label">' + esc(this._t('applied_filters', 'Applied filters')) + ':</span>' +
        chips.join('') +
        '<button type="button" class="collection-chips__clear link" data-filter-clear>' +
        esc(this._t('clear_filters', 'Clear all')) + '</button>';
    }

    _removeChip(chip) {
      var facet = chip.getAttribute('data-facet');
      if (facet === 'price') {
        this.state.price = { min: null, max: null };
        var minEl = this.querySelector('[data-filter-range-min]');
        var maxEl = this.querySelector('[data-filter-range-max]');
        if (minEl) minEl.value = '';
        if (maxEl) maxEl.value = '';
      } else {
        var value = chip.getAttribute('data-value');
        if (this.state.filters[facet]) this.state.filters[facet].delete(value);
        var input = this.querySelector('input[data-facet="' + facet + '"][value="' + (window.CSS && CSS.escape ? CSS.escape(value) : value) + '"]');
        if (input) input.checked = false;
      }
      this._afterChange();
    }

    _clearAll() {
      this.state.filters = {};
      this.state.price = { min: null, max: null };
      this.querySelectorAll('input[data-facet]').forEach(function (i) { i.checked = false; });
      var minEl = this.querySelector('[data-filter-range-min]');
      var maxEl = this.querySelector('[data-filter-range-max]');
      if (minEl) minEl.value = '';
      if (maxEl) maxEl.value = '';
      this._afterChange();
    }

    /* --- Sort (client-side reorder) --- */

    _applySort() {
      var s = this.state ? this.state.sort : null;
      var sorted = this.products.slice();
      sorted.sort(function (a, b) {
        switch (s) {
          case 'title-ascending': return a.title.localeCompare(b.title);
          case 'title-descending': return b.title.localeCompare(a.title);
          case 'price-ascending': return a.price - b.price || a.order - b.order;
          case 'price-descending': return b.price - a.price || a.order - b.order;
          case 'created-ascending': return a.created - b.created || a.order - b.order;
          case 'created-descending': return b.created - a.created || a.order - b.order;
          default: return a.order - b.order; // manual / featured / best-selling
        }
      });
      var frag = document.createDocumentFragment();
      sorted.forEach(function (p) { frag.appendChild(p.el); });
      this.grid.appendChild(frag);
    }

    _bindSort() {
      var self = this;
      var sort = this.querySelector('[data-filter-sort]');
      if (!sort) return;
      sort.addEventListener('change', function () {
        var opt = sort.options[sort.selectedIndex];
        self.state.sort = (opt && opt.getAttribute('data-sort-value')) || 'manual';
        self._applySort();
        self._syncUrl();
      });
    }

    _revealSidebar() {
      var hasGroups = this.querySelector('[data-filter-group]');
      if (!hasGroups || this.sidebarPos === 'none') return;
      this.classList.remove('collection-layout--no-sidebar');
      this.classList.add('collection-layout--' + this.sidebarPos);
    }

    /* --- Visually-hidden live region for loading/result status (WCAG 4.1.3) --- */

    _announceStatus(message) {
      if (!this._statusNode) {
        var node = document.createElement('p');
        node.className = 'visually-hidden';
        node.setAttribute('role', 'status');
        node.setAttribute('aria-live', 'polite');
        this.appendChild(node);
        this._statusNode = node;
      }
      this._statusNode.textContent = message || '';
    }

    /* =========================================================
       Drawer (mobile)
       ========================================================= */

    _openDrawer() {
      var sidebar = this.querySelector('[data-filter-sidebar]');
      var backdrop = this.querySelector('.collection-sidebar__backdrop');
      var trigger = this.querySelector('[data-filter-drawer-open]');
      if (!sidebar) return;

      sidebar.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-open');
      document.body.classList.add('filter-drawer-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');

      this._lastTrigger = trigger;
      this._trapFocus(sidebar);

      if (!this._escHandler) {
        var self = this;
        this._escHandler = function (e) { if (e.key === 'Escape') self._closeDrawer(); };
        document.addEventListener('keydown', this._escHandler);
      }
    }

    _closeDrawer() {
      var sidebar = this.querySelector('[data-filter-sidebar]');
      var backdrop = this.querySelector('.collection-sidebar__backdrop');
      var trigger = this._lastTrigger || this.querySelector('[data-filter-drawer-open]');
      if (!sidebar) return;

      sidebar.classList.remove('is-open');
      if (backdrop) backdrop.classList.remove('is-open');
      document.body.classList.remove('filter-drawer-open');
      if (trigger) { trigger.setAttribute('aria-expanded', 'false'); trigger.focus(); }

      if (this._escHandler) {
        document.removeEventListener('keydown', this._escHandler);
        this._escHandler = null;
      }
    }

    _trapFocus(container) {
      var focusable = container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      first.focus();

      if (this._trapHandler) container.removeEventListener('keydown', this._trapHandler);
      this._trapHandler = function (e) {
        if (e.key !== 'Tab') return;
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      };
      container.addEventListener('keydown', this._trapHandler);
    }

    /* =========================================================
       Truncation toggle + in-filter search + collapse state
       ========================================================= */

    _toggleGroupTruncation(toggle) {
      var group = toggle.closest('[data-filter-group]');
      if (!group) return;
      var expanded = toggle.getAttribute('aria-expanded') === 'true';
      group.querySelectorAll('[data-truncated]').forEach(function (item) {
        item.classList.toggle('is-hidden', expanded);
      });
      toggle.setAttribute('aria-expanded', String(!expanded));
      var more = toggle.querySelector('[data-filter-toggle-more]');
      var less = toggle.querySelector('[data-filter-toggle-less]');
      if (more) more.hidden = !expanded;
      if (less) less.hidden = expanded;
    }

    _filterValuesInGroup(input) {
      var group = input.closest('[data-filter-group]');
      if (!group) return;
      var query = input.value.trim().toLowerCase();
      group.querySelectorAll('[data-filter-item]').forEach(function (item) {
        var label = item.getAttribute('data-filter-value') || '';
        var match = query === '' || label.indexOf(query) !== -1;
        item.classList.toggle('is-filtered-out', !match);
      });
    }

    _bindCollapseState() {
      var saved = this._readCollapseState();
      var self = this;
      this.querySelectorAll('[data-filter-group]').forEach(function (group) {
        var key = group.getAttribute('data-filter-key');
        if (!key) return;
        if (saved.hasOwnProperty(key)) group.open = saved[key];
        group.addEventListener('toggle', function () {
          var state = self._readCollapseState();
          state[key] = group.open;
          try { sessionStorage.setItem(COLLAPSE_STORE_KEY, JSON.stringify(state)); } catch (e) {}
        });
      });
    }

    _readCollapseState() {
      try {
        var raw = sessionStorage.getItem(COLLAPSE_STORE_KEY);
        return raw ? JSON.parse(raw) : {};
      } catch (e) {
        return {};
      }
    }
  }

  customElements.define('collection-filters', CollectionFilters);
})();
